/* eslint-disable max-len */
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useServiceEventCompletion } from '../hooks/useServiceEventCompletion';
import { WorkSummaryCard } from './WorkSummaryCard';
import { addressPartsFromRecall, formatStreetAddress } from '../utils/addressFormat';
import type { RouteRecallDetail } from '@/types/routeTracking';

interface WorkSummaryDrawerProps {
  recall: RouteRecallDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatCompletedAt(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

// Right-anchored side drawer over the existing route tracker list. Opens when a service
// advisor clicks a completed recall row; renders the captured Work Summary alongside
// recall identifiers (campaign / VIN / dealer / completion time / tech).
//
// Silent no-op when the service event hasn't replicated yet — matches the iOS recall
// work screen behavior (no spinner, no empty state, no layout shift).
export function WorkSummaryDrawer({ recall, open, onOpenChange }: WorkSummaryDrawerProps) {
  const { data: event } = useServiceEventCompletion(recall?.assignmentId);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className='fixed inset-0 z-[9998] bg-black/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
        <DialogPrimitive.Content
          className='fixed right-0 top-0 z-[9999] flex h-full w-full max-w-md flex-col overflow-hidden bg-gray-50 shadow-2xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          aria-describedby={undefined}
        >
          <DialogPrimitive.Title className='sr-only'>Work summary details</DialogPrimitive.Title>

          {/* Header */}
          <div className='flex items-start justify-between gap-3 border-b border-gray-200 bg-white px-5 py-4'>
            <div className='min-w-0'>
              <div className='text-xs font-medium uppercase tracking-wider text-gray-500'>
                Completed Recall
              </div>
              <div className='mt-1 truncate text-base font-semibold text-gray-900'>
                {recall?.campaignNumber ?? '—'}
              </div>
              {/* Three small clusters with visible gaps between groups:
                  1) Vehicle identifier (VIN), 2) Where (dealer + address),
                  3) Audit (completed when / by). Inside each group lines stay
                  tight so the cluster reads as one. */}
              <div className='mt-1 text-xs text-gray-600'>
                <div>
                  VIN <span className='font-mono'>{recall?.vin ?? '—'}</span>
                </div>
                {recall?.dealerName && (
                  <div className='mt-2.5'>
                    <div className='truncate font-medium'>{recall.dealerName}</div>
                    {(() => {
                      const street = formatStreetAddress(addressPartsFromRecall(recall));
                      return street ? <div className='text-gray-500'>{street}</div> : null;
                    })()}
                  </div>
                )}
                {recall?.completedAt && (
                  <div className='mt-2.5'>Completed {formatCompletedAt(recall.completedAt)}</div>
                )}
                {recall?.completedByUserName && (
                  <div className='mt-0.5'>By {recall.completedByUserName}</div>
                )}
              </div>
            </div>
            <DialogPrimitive.Close
              aria-label='Close work summary'
              className='shrink-0 rounded-md p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700'
            >
              <X className='h-5 w-5' />
            </DialogPrimitive.Close>
          </div>

          {/* Body */}
          <div className='flex-1 overflow-y-auto px-5 py-5'>
            {event ? (
              <WorkSummaryCard event={event} />
            ) : (
              <p className='text-sm text-gray-500'>
                Work details will appear here once the technician&apos;s submission has synced.
              </p>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
